import { useEffect, useState } from 'react';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { useSchedule, type ScheduleEvent } from '@/hooks/useSchedule';
import '@/styles/register.css';
import '@/styles/schedule.css';
import { formatScheduleCountdown, formatScheduleEventTime } from '@/lib/scheduleCountdown';

interface RowCountdownState {
  text: string;
  active: boolean;
}

function buildInitialRowState(
  schedules: ScheduleEvent[],
  runningLabel: string,
): Record<number, RowCountdownState> {
  const now = Math.floor(Date.now() / 1000);
  const next: Record<number, RowCountdownState> = {};

  schedules.forEach((event, index) => {
    const serverActive = Boolean(event.IsActive);
    if (serverActive) {
      next[index] = { text: runningLabel, active: true };
      return;
    }

    const diff = event.event_timestamp - now;
    if (diff <= 0) {
      next[index] = { text: runningLabel, active: true };
    } else {
      next[index] = { text: formatScheduleCountdown(diff), active: false };
    }
  });

  return next;
}

export function SchedulePage() {
  const { t } = useLocale();
  const { data: schedules, isLoading, isError } = useSchedule();
  const runningLabel = t('sched_running');

  const [rowState, setRowState] = useState<Record<number, RowCountdownState>>({});

  useEffect(() => {
    if (!schedules?.length) {
      setRowState({});
      return;
    }

    const update = () => {
      setRowState((prev) => {
        const now = Math.floor(Date.now() / 1000);
        const next: Record<number, RowCountdownState> = {};

        schedules.forEach((event, index) => {
          const wasActive = prev[index]?.active ?? Boolean(event.IsActive);
          if (wasActive) {
            next[index] = { text: runningLabel, active: true };
            return;
          }

          const diff = event.event_timestamp - now;
          if (diff <= 0) {
            next[index] = { text: runningLabel, active: true };
          } else {
            next[index] = { text: formatScheduleCountdown(diff), active: false };
          }
        });

        return next;
      });
    };

    setRowState(buildInitialRowState(schedules, runningLabel));
    update();
    const intervalId = window.setInterval(update, 1000);
    return () => window.clearInterval(intervalId);
  }, [schedules, runningLabel]);

  return (
    <div className="container-ingame">
      <div className="calendar-container">
        <SimpleMenu title={t('schedule')} />

        <div className="event-register schedule-content">
          {isLoading ? (
            <div style={{ textAlign: 'center', color: '#a97c50', fontSize: '1.2rem', padding: '40px' }}>
              {t('loading_data')}
            </div>
          ) : isError ? (
            <div style={{ textAlign: 'center', color: '#a97c50', fontSize: '1.2rem', padding: '40px' }}>
              {t('sched_load_error')}
            </div>
          ) : schedules && schedules.length > 0 ? (
            <div className="schedule-inline-list" role="list">
              {schedules.map((event, index) => {
                const state = rowState[index];
                const isActive = state?.active ?? Boolean(event.IsActive);
                const countdownText = state?.text ?? (isActive ? runningLabel : '');
                const eventType = event.EventType?.trim() ?? '';

                return (
                  <div
                    key={`${event.id}-${index}`}
                    role="listitem"
                    className={`schedule-inline-row${isActive ? ' is-active' : ''}`}
                  >
                    <div className="schedule-inline-identity">
                      <span className="schedule-inline-name">{event.EventName}</span>
                      {eventType ? <span className="schedule-inline-type">{eventType}</span> : null}
                    </div>
                    <div className="schedule-inline-timing">
                      {isActive ? <span className="schedule-inline-active">{runningLabel}</span> : <>
                        <span className="schedule-inline-time">{formatScheduleEventTime(event.EventTime)}</span>
                        <span className="schedule-inline-countdown">{countdownText}</span>
                      </>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#a97c50', fontSize: '1.2rem', padding: '40px' }}>
              {t('sched_no_events')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
